import React, { useState, useEffect, useMemo } from 'react';
import { paymentService } from '../services/paymentService';
import { triggerGlobalSubscriptionRefresh, useSubscriptionContext } from '../contexts/SubscriptionContext';
import InlinePaymentForm from './InlinePaymentForm';
import { addonService } from '../services/addonService';
import { AvailableAddon } from '../types/subscription';

interface ServiceContent {
  title: string;
  summary: string;
  description: string;
  benefits: string[];
}

interface SubscriptionUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  requiredFeature: 'essential' | 'gold' | 'premium';
  featureName: string;
  selectedService?: string | null;
  serviceContent?: ServiceContent | null;
}


const SubscriptionUpgradeModal: React.FC<SubscriptionUpgradeModalProps> = ({
  isOpen,
  onClose,
  requiredFeature,
  featureName,
  selectedService,
  serviceContent
}) => {
  const { subscription, hasAddon } = useSubscriptionContext();
  type PlanType = 'essential' | 'gold' | 'premium' | 'standalone_tabletop';
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(requiredFeature);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showInlinePayment, setShowInlinePayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'inline' | 'redirect'>('inline');
  const [availableAddon, setAvailableAddon] = useState<AvailableAddon | null>(null);
  const [isLoadingAddon, setIsLoadingAddon] = useState(false);
  const [includeTabletopAddon, setIncludeTabletopAddon] = useState(false);
  const [standaloneCreditsPerPack, setStandaloneCreditsPerPack] = useState(1);

  // Show the one-time $500 tabletop option only when the modal was opened in a Tabletop context
  const showStandaloneOption = selectedService === 'Tabletop Scenarios';
  const isStandalone = selectedPlan === 'standalone_tabletop';
  const showTabletopToggle = !hasAddon('tabletop_creator') && selectedPlan !== 'premium' && !isStandalone;

  // Auto-uncheck addon when switching to Premium
  useEffect(() => {
    if (selectedPlan === 'premium') {
      setIncludeTabletopAddon(false);
    }
  }, [selectedPlan]);

  // Load addon info when the modal opens so it's ready for the toggle
  useEffect(() => {
    if (!isOpen || hasAddon('tabletop_creator') || availableAddon || isLoadingAddon) {
      return;
    }
    setIsLoadingAddon(true);
    addonService.getAddonInfo('tabletop_creator').then(addon => {
      if (addon) {
        setAvailableAddon(addon);
      } else {
        console.warn('Failed to load tabletop_creator addon info - addon not found');
      }
    }).catch(error => {
      console.error('Error loading addon info:', error);
      // Even if API fails, we can still show the addon option with default info
      // The AddonPurchaseModal will handle loading the addon info
    }).finally(() => {
      setIsLoadingAddon(false);
    });
  }, [isOpen, availableAddon, isLoadingAddon, hasAddon]);

  useEffect(() => {
    if (!isOpen || !showStandaloneOption) return;
    paymentService
      .getStandaloneTabletopCredits()
      .then((d) => setStandaloneCreditsPerPack(d.credits_per_pack ?? 1))
      .catch(() => setStandaloneCreditsPerPack(1));
  }, [isOpen, showStandaloneOption]);

  const plans = useMemo(() => {
    type Plan = {
      type: PlanType;
      name: string;
      price: number;
      features: string[];
      billingLabel?: string;
      tag?: string;
    };
    const base: Plan[] = [
      {
        type: 'essential',
        name: 'Essential Plan',
        price: 799,
        features: [
          'BCP aligned with ISO 22301:2019 (Flash AI)',
          'Incident response checklists & SOPs',
          'Basic customer support'
        ]
      },
      {
        type: 'gold',
        name: 'Gold Plan',
        price: 1999,
        features: [
          'All Essential Features',
          'Flash RTC out-of-band communication',
          'Dedicated onboarding support'
        ]
      },
      {
        type: 'premium',
        name: 'Premium Plan',
        price: 3999,
        features: [
          'All Gold Features',
          'Executive leadership training modules',
          'ISO 27001 readiness (cybersecurity)',
          'Customized tabletop scenarios & insurer reports',
          'Resilience Badge (up to 10% insurance discount)',
          'Priority support & quarterly check-ins'
        ]
      }
    ];
    if (showStandaloneOption) {
      const n = standaloneCreditsPerPack;
      base.push({
        type: 'standalone_tabletop',
        name: 'Standalone Tabletop',
        price: 500,
        billingLabel: n <= 1 ? 'one-time / 1 exercise' : `one-time / ${n} exercises`,
        tag: 'No subscription',
        features: [
          n <= 1 ? '1 AI-generated tabletop scenario' : `${n} AI-generated tabletop scenarios`,
          'Pre-filled DOCX worksheet to run the exercise',
          'Optional ISO 22301 alignment review (we notify Cowbell)',
          'Works with or without an existing BCP',
          'No yearly subscription required'
        ]
      });
    }
    return base;
  }, [showStandaloneOption, standaloneCreditsPerPack]);

  // Update selected plan when requiredFeature changes
  useEffect(() => {
    // Set to required feature or higher if current selection is lower.
    // Standalone is treated as a parallel one-off path — never auto-bumped from the hierarchy.
    if (selectedPlan === 'standalone_tabletop') return;
    const planHierarchy = ['essential', 'gold', 'premium'];
    const currentLevel = planHierarchy.indexOf(selectedPlan);
    const requiredLevel = planHierarchy.indexOf(requiredFeature);

    if (currentLevel < requiredLevel) {
      setSelectedPlan(requiredFeature);
    }
  }, [requiredFeature, selectedPlan]);

  // Prevent stale standalone selection leaking into non-tabletop modal contexts.
  useEffect(() => {
    if (!showStandaloneOption && selectedPlan === 'standalone_tabletop') {
      setSelectedPlan(requiredFeature);
    }
  }, [showStandaloneOption, selectedPlan, requiredFeature]);

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    if (isLoading) return;

    // Standalone $500 tabletop pack: always Stripe redirect (one-time payment), no subscription change.
    if (isStandalone) {
      setIsLoading(true);
      setError(null);
      try {
        const balance = await paymentService.getStandaloneTabletopCredits().catch(() => null);
        const pack = balance?.credits_per_pack ?? standaloneCreditsPerPack;
        if (balance && (balance.credits_remaining ?? 0) >= pack) {
          setError(
            `You already have ${balance.credits_remaining} unused tabletop credits. Use some before buying another pack.`
          );
          setIsLoading(false);
          return;
        }
        const data = await paymentService.createStandaloneTabletopCheckoutSession();
        window.location.href = data.checkout_url;
      } catch (err: any) {
        setError(err?.message || 'Could not start checkout');
        setIsLoading(false);
      }
      return;
    }

    if (paymentMethod === 'inline') {
      setShowInlinePayment(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Check if user has existing subscription
      if (subscription && subscription.id) {
        // User has existing subscription - upgrade it
        await paymentService.changeSubscriptionPlan(subscription.id, selectedPlan as 'essential' | 'gold' | 'premium');
        setSuccess(`Successfully upgraded to ${plans.find(p => p.type === selectedPlan)?.name}!`);
        triggerGlobalSubscriptionRefresh();
        // Close modal after a short delay to show success message
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        const response = await paymentService.createCheckoutSession(
          selectedPlan as 'essential' | 'gold' | 'premium',
          includeTabletopAddon ? 'tabletop_creator' : undefined
        );
        window.location.href = response.checkout_url;
      }
    } catch (err) {
      let errorMessage = 'Subscription failed';

      if (err instanceof Error) {
        // Handle specific error cases
        if (err.message.includes('already has an active subscription')) {
          errorMessage = 'You already have an active subscription. Please contact support if you need to change your plan.';
        } else if (err.message.includes('Invalid plan type')) {
          errorMessage = 'The selected plan is not available. Please try again.';
        } else if (err.message.includes('Subscription not found')) {
          errorMessage = 'Your subscription could not be found. Please refresh the page and try again.';
        } else if (err.message.includes('Failed to change subscription plan')) {
          errorMessage = 'Unable to upgrade your subscription. Please try again or contact support.';
        } else if (err.message.includes('canceled subscription')) {
          errorMessage = 'Your subscription was canceled. The system will create a new subscription for you.';
        } else {
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
      console.error('Subscription error:', err);
      setIsLoading(false);
    }
  };

  const handleInlinePaymentSuccess = (subscription: any) => {
    setShowInlinePayment(false);
    triggerGlobalSubscriptionRefresh();
    onClose();
  };

  const handleInlinePaymentError = (error: string) => {
    setError(error);
  };

  const handleInlinePaymentCancel = () => {
    setShowInlinePayment(false);
  };

  return (
    <div className="overflow-y-auto fixed inset-0 z-50">
      <div className="flex justify-center items-center p-4 min-h-screen">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Complete Your Subscription</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* FlashRTC Information Link */}
            <div className="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-blue-900">FlashRTC Information Guide</h3>
                    <p className="text-xs text-blue-700">Learn about out-of-band communication features</p>
                  </div>
                </div>
                <a
                  href="/FlashRTCInformation.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-white rounded-md border border-blue-300 transition-colors duration-200 hover:bg-blue-50 hover:border-blue-400"
                >
                  <svg className="mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  View Guide
                </a>
              </div>
            </div>
            {showInlinePayment ? (
              <InlinePaymentForm
                planType={selectedPlan as 'essential' | 'gold' | 'premium'}
                planName={
                  includeTabletopAddon
                    ? `${plans.find(p => p.type === selectedPlan)?.name} + Tabletop Creator`
                    : plans.find(p => p.type === selectedPlan)?.name || ''
                }
                planPrice={
                  (plans.find(p => p.type === selectedPlan)?.price || 0) +
                  (includeTabletopAddon ? (availableAddon?.price_dollars || 499) : 0)
                }
                onSuccess={handleInlinePaymentSuccess}
                onError={handleInlinePaymentError}
                onCancel={handleInlinePaymentCancel}
                bundleAddonType={includeTabletopAddon ? 'tabletop_creator' : undefined}
              />
            ) : (
              <>
                <div className="mb-6">
                  <h3 className="mb-2 text-lg font-medium text-gray-900">Choose Your Plan</h3>
                  {serviceContent ? (
                    <div className="p-4 mb-4 bg-blue-50 rounded-lg border border-blue-200">
                      <h4 className="mb-2 font-semibold text-blue-900">{serviceContent.title}</h4>
                      <p className="mb-3 text-sm text-blue-800">{serviceContent.description}</p>
                      <div className="text-blue-700">
                        <p className="mb-2 text-xs font-medium">Benefits to Customers:</p>
                        <ul className="space-y-1 text-xs">
                          {serviceContent.benefits.map((benefit, index) => (
                            <li key={index} className="flex items-start">
                              <span className="mr-2">•</span>
                              <span>{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-600">
                      <span className="font-medium text-blue-600">{featureName}</span> requires{' '}
                      <span className="font-medium text-blue-600">{plans.find(p => p.type === requiredFeature)?.name}</span> or higher
                    </p>
                  )}
                </div>

                {/* Plan Selection */}
                <div className={`grid grid-cols-1 gap-4 mb-6 ${plans.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
                  {plans.map((plan) => {
                    const isStandaloneCard = plan.type === 'standalone_tabletop';
                    const isRequired = plan.type === requiredFeature;
                    const isHigher = ['essential', 'gold', 'premium'].indexOf(plan.type) >= ['essential', 'gold', 'premium'].indexOf(requiredFeature);
                    // Standalone is always selectable (parallel one-off path); subscription tiers obey the hierarchy.
                    const isAvailable = isStandaloneCard || isRequired || isHigher;

                    return (
                      <div
                        key={plan.type}
                        className={`relative p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedPlan === plan.type
                            ? 'border-blue-500 bg-blue-50'
                            : isAvailable
                            ? 'border-gray-200 hover:border-gray-300'
                            : 'border-gray-200 opacity-50 cursor-not-allowed'
                        }`}
                        onClick={() => isAvailable && setSelectedPlan(plan.type)}
                      >
                        {isRequired && !isStandaloneCard && (
                          <div className="absolute top-2 left-2">
                            <span className="px-2 py-1 text-xs font-medium text-white bg-green-500 rounded-full">
                              Required
                            </span>
                          </div>
                        )}
                        {isStandaloneCard && plan.tag && (
                          <div className="absolute top-2 left-2">
                            <span className="px-2 py-1 text-xs font-medium text-white bg-purple-600 rounded-full">
                              {plan.tag}
                            </span>
                          </div>
                        )}
                        {selectedPlan === plan.type && (
                          <div className="absolute top-2 right-2">
                            <div className="flex justify-center items-center w-5 h-5 bg-blue-500 rounded-full">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                        )}

                      <div className="text-center">
                        <h4 className="font-semibold text-gray-900">{plan.name}</h4>
                        <div className={`mt-2 text-2xl font-bold ${isStandaloneCard ? 'text-purple-700' : 'text-blue-600'}`}>
                          ${plan.price}
                          <span className="text-sm font-normal text-gray-500">
                            {plan.billingLabel ? ` ${plan.billingLabel}` : '/year'}
                          </span>
                        </div>
                      </div>

                      <ul className="mt-4 space-y-2">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600">
                            <svg className="flex-shrink-0 mr-2 w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    );
                  })}
                </div>

                {/* Payment Method Selection — hidden for one-time standalone (always Stripe redirect) */}
                {!isStandalone && (
                  <div className="mb-6">
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Payment Method
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="inline"
                          checked={paymentMethod === 'inline'}
                          onChange={(e) => setPaymentMethod(e.target.value as 'inline' | 'redirect')}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Pay directly on this page</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="redirect"
                          checked={paymentMethod === 'redirect'}
                          onChange={(e) => setPaymentMethod(e.target.value as 'inline' | 'redirect')}
                          className="mr-2"
                        />
                        <span className="text-sm text-gray-700">Redirect to Stripe Checkout</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Tabletop Add-On Toggle */}
                {showTabletopToggle && (
                  <div
                    className={`p-4 mb-6 rounded-lg border cursor-pointer transition-all ${
                      includeTabletopAddon
                        ? 'bg-purple-50 border-purple-300'
                        : 'bg-gray-50 border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setIncludeTabletopAddon(!includeTabletopAddon)}
                  >
                    <label className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={includeTabletopAddon}
                          onChange={(e) => setIncludeTabletopAddon(e.target.checked)}
                          className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-900">Add Tabletop Creator</h4>
                          <p className="text-sm text-gray-600">2 customized tabletop scenarios + insurer reports</p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-purple-700 whitespace-nowrap ml-4">
                        +${availableAddon?.price_dollars?.toFixed(0) || '499'}/yr
                      </span>
                    </label>
                  </div>
                )}

                {/* Upgrade Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleUpgrade}
                    disabled={isLoading || !!success}
                    className={`px-8 py-3 font-semibold rounded-lg transition-all ${
                      success
                        ? 'text-white bg-green-600 cursor-default'
                        : 'text-white bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed'
                    }`}
                  >
                    {success
                      ? '✓ Upgrade Complete!'
                      : isLoading
                        ? (isStandalone ? 'Redirecting to payment…' : 'Processing...')
                        : isStandalone
                          ? (standaloneCreditsPerPack <= 1
                              ? 'Buy tabletop credit — $500 (one-time)'
                              : `Buy ${standaloneCreditsPerPack}-Pack — $500 (one-time)`)
                          : subscription && subscription.id
                            ? `Upgrade to ${plans.find(p => p.type === selectedPlan)?.name}`
                            : `Get Started — $${
                                (plans.find(p => p.type === selectedPlan)?.price || 0) +
                                (includeTabletopAddon ? (availableAddon?.price_dollars || 499) : 0)
                              }/yr`
                    }
                  </button>
                </div>

                {error && (
                  <div className="p-3 mt-4 bg-red-50 rounded-md border border-red-200">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                {success && (
                  <div className="p-3 mt-4 bg-green-50 rounded-md border border-green-200">
                    <p className="text-sm text-green-600">{success}</p>
                  </div>
                )}

                {/* Additional Info */}
                <div className="mt-6 text-sm text-center text-gray-500">
                  <p>Need help? <button className="text-blue-600 hover:underline">Contact Support</button></p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SubscriptionUpgradeModal;
